/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import {
  SYNCGROUPS_TYPE_SETFEATURES,
  SYNCGROUPS_TYPE_SETTIME,
  SYNCGROUPS_TYPE_SETBBOX,
} from './constants';
import type { WorkspaceViewsById } from './featureGroupUtils';
import {
  getFeatureSourceIds,
  getFeatureSourcePanelId,
  getMapSourceIds,
  isFeatureSource,
  shouldRemoveFeatureGroupForPanel,
} from './featureGroupUtils';
import { getFeatureGroupId } from './featureLinks';
import { getFeatureSyncLayersForPanel } from './featureSyncLayers';
import { syncGroupsActions } from './reducer';
import { createSyncGroupViewStateSelector } from './selector';
import { getSynchronizationGroup } from './selectors';
import type {
  SyncGroupAddTargetPayload,
  SyncGroupLinkTargetPayload,
  SynchronizationGroupState,
  SynchronizationGroupModuleState,
} from './types';
import { layerActions } from '../../map/layer/reducer';
import type { LayerState } from '../../map/layer/types';
import { setBboxSync, setTimeSync } from '../synchronizationActions/actions';
import type { SetBboxPayload, SetTimePayload } from '../types';

export const syncGroupsListener =
  createListenerMiddleware<SynchronizationGroupModuleState>();
const ensureFeatureGroupsForSources = (
  state: SynchronizationGroupState,
  rootState: SynchronizationGroupModuleState,
  dispatch: (action: unknown) => void,
): void => {
  const globalState = rootState as SynchronizationGroupModuleState & {
    workspace?: {
      views?: {
        byId?: WorkspaceViewsById;
      };
    };
  };
  const views = globalState.workspace?.views?.byId;

  const featureSourceIds = getFeatureSourceIds(state);

  featureSourceIds.forEach((panelId) => {
    const groupId = getFeatureGroupId(panelId);
    if (!state.groups.byId[groupId]) {
      // panelId comes from getFeatureSourceIds, so it is an active feature source.
      if (shouldRemoveFeatureGroupForPanel(panelId, views, true)) {
        return;
      }
      dispatch(
        syncGroupsActions.syncGroupAddGroup({
          groupId,
          title: 'Features',
          type: SYNCGROUPS_TYPE_SETFEATURES,
          sourcePanelId: panelId,
        }),
      );
    }
  });

  state.groups.allIds.forEach((groupId) => {
    const group = state.groups.byId[groupId];
    if (group?.type !== SYNCGROUPS_TYPE_SETFEATURES) {
      return;
    }

    const panelId = getFeatureSourcePanelId(group);
    if (!panelId) {
      return;
    }

    const source = state.sources.byId[panelId];
    // Keep feature groups for unknown sources (pending preset links),
    // but remove groups for known non-feature sources (e.g. map panels).
    if (source && !isFeatureSource(source)) {
      dispatch(syncGroupsActions.syncGroupRemoveGroup({ groupId }));
      return;
    }

    if (
      shouldRemoveFeatureGroupForPanel(panelId, views, isFeatureSource(source))
    ) {
      dispatch(syncGroupsActions.syncGroupRemoveGroup({ groupId }));
    }
  });
};

const pruneNonMapTargetsFromFeatureGroups = (
  state: SynchronizationGroupState,
  dispatch: (action: unknown) => void,
): void => {
  const mapSourceIds = getMapSourceIds(state);
  state.groups.allIds.forEach((groupId) => {
    const group = state.groups.byId[groupId];
    if (group?.type !== SYNCGROUPS_TYPE_SETFEATURES) {
      return;
    }

    group.targets.allIds.forEach((targetId) => {
      const targetSource = state.sources.byId[targetId];
      if (targetSource && !mapSourceIds.has(targetId)) {
        dispatch(
          syncGroupsActions.syncGroupRemoveTarget({
            groupId,
            targetId,
          }),
        );
      }
    });
  });
};

const reconcileFeatureGroups = (
  state: SynchronizationGroupState,
  rootState: SynchronizationGroupModuleState,
  dispatch: (action: unknown) => void,
): void => {
  ensureFeatureGroupsForSources(state, rootState, dispatch);
  pruneNonMapTargetsFromFeatureGroups(state, dispatch);
};

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddTarget,
    syncGroupsActions.syncGroupLinkTarget,
  ),
  effect: async ({ payload, type }, listenerApi) => {
    listenerApi.cancelActiveListeners();

    const {
      groupId,
      linked,
      targetId: targetToUpdate,
    } = payload as SyncGroupAddTargetPayload | SyncGroupLinkTargetPayload;

    const group = getSynchronizationGroup(listenerApi.getState(), groupId);

    if (!linked && group) {
      switch (group.type) {
        case SYNCGROUPS_TYPE_SETTIME:
          // A local panel should keep its own current time after unlinking from timeslider sync.
          if (type === syncGroupsActions.syncGroupLinkTarget.type) {
            break;
          }
          if (
            !group.targets.allIds.some(
              (targetId) =>
                targetId !== targetToUpdate &&
                group.targets.byId[targetId]?.linked,
            )
          ) {
            break;
          }
          if (!group.payloadByType[SYNCGROUPS_TYPE_SETTIME]) {
            return;
          }
          listenerApi.dispatch(
            setTimeSync(
              group.payloadByType[SYNCGROUPS_TYPE_SETTIME] as SetTimePayload,
              [
                {
                  targetId: targetToUpdate,
                  value: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETTIME
                    ] as SetTimePayload
                  ).value,
                },
              ],
              [groupId],
            ),
          );
          break;
        case SYNCGROUPS_TYPE_SETBBOX:
          if (!group.payloadByType[SYNCGROUPS_TYPE_SETBBOX]) {
            return;
          }
          listenerApi.dispatch(
            setBboxSync(
              group.payloadByType[SYNCGROUPS_TYPE_SETBBOX] as SetBboxPayload,
              [
                {
                  targetId: targetToUpdate,
                  bbox: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETBBOX
                    ] as SetBboxPayload
                  ).bbox,
                  srs: (
                    group.payloadByType[
                      SYNCGROUPS_TYPE_SETBBOX
                    ] as SetBboxPayload
                  ).srs,
                },
              ],
              [groupId],
            ),
          );
          break;
        default:
          break;
      }
    }
  },
});

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddGroup,
    syncGroupsActions.syncGroupRemoveGroup,
    syncGroupsActions.syncGroupAddSource,
    syncGroupsActions.syncGroupRemoveSource,
    syncGroupsActions.setLinkedMap,
    syncGroupsActions.updateLinkedMap,
  ),
  effect: async (_, listenerApi) => {
    const rootState = listenerApi.getState();
    const state = rootState.syncGroups;
    if (!state) {
      return;
    }
    reconcileFeatureGroups(state, rootState, listenerApi.dispatch);
  },
});

syncGroupsListener.startListening({
  matcher: isAnyOf(
    syncGroupsActions.syncGroupAddGroup,
    syncGroupsActions.syncGroupRemoveGroup,
    syncGroupsActions.syncGroupAddSource,
    syncGroupsActions.syncGroupRemoveSource,
    syncGroupsActions.syncGroupAddTarget,
    syncGroupsActions.syncGroupRemoveTarget,
    syncGroupsActions.syncGroupLinkTarget,
  ),
  effect: async (_, listenerApi) => {
    const viewState = createSyncGroupViewStateSelector(listenerApi.getState());
    const currentViewState = listenerApi.getState().syncGroups?.viewState;
    if (!currentViewState || !isEqual(currentViewState, viewState)) {
      listenerApi.dispatch(
        syncGroupsActions.syncGroupSetViewState({ viewState }),
      );
    }
  },
});

type FullStateWithLayers = SynchronizationGroupModuleState & {
  layers?: LayerState;
};

// cleans up linked sources for a panel when its removed. (eg an overlayer that added features to a panel)
syncGroupsListener.startListening({
  actionCreator: syncGroupsActions.syncGroupRemoveSource,
  effect: async (action, listenerApi) => {
    const layersState = (listenerApi.getState() as FullStateWithLayers).layers;
    getFeatureSyncLayersForPanel(layersState, action.payload.id).forEach(
      ({ layerId, mapId }) => {
        listenerApi.dispatch(layerActions.layerDelete({ layerId, mapId }));
      },
    );
  },
});
